import { useState } from 'react';
import axios from 'axios';

import {
    BaseURL,
} from '../../config/apiUrl';

const API_BASE_URL = BaseURL('financial');

const useFinancialAPI = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fileId, setFileId] = useState(null);
    const [excelData, setExcelData] = useState(null);
    const [businessType, setBusinessType] = useState(null);
    const [expenseHeads, setExpenseHeads] = useState(null);
    const [accountHeads, setAccountHeads] = useState(null);
    const [accountHeadCategories, setAccountHeadCategories] = useState({});

    const getAccountHeads = async (files, fileParams, bizType, weightedData = null) => {
        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();

            files.forEach(file => {
                formData.append('files', file);
            });

            formData.append('fileParams', JSON.stringify(fileParams));
            if (bizType) {
                formData.append('bizType', bizType);
            }

            // Add weighted data if provided
            if (weightedData) {
                formData.append('weightedData', JSON.stringify(weightedData));
            }

            formData.append('getAccountHeads', 'true');

            const response = await axios.post(
                `${API_BASE_URL}/analyze`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );

            if (response.data.success) {
                setAccountHeads(response.data.accountHeads);
                // Store the auto-categorization data if it exists
                if (response.data.accountHeadCategories) {
                    setAccountHeadCategories(response.data.accountHeadCategories);
                }
                setBusinessType(response.data.businessType);
                return response.data.accountHeads;
            } else {
                throw new Error(response.data.error || 'Error retrieving account heads');
            }
        } catch (err) {
            const message = err.response?.data?.error || err.message || 'Error retrieving account heads';
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    };

    const getExpenseHeads = async (
        files,
        fileParams,
        bizType,
        accountHeadMappings = null,
        conflictHeaders = null,
        weightedData = null
    ) => {
        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();

            files.forEach(file => {
                formData.append('files', file);
            });

            formData.append('fileParams', JSON.stringify(fileParams));
            if (bizType) {
                formData.append('bizType', bizType);
            }

            // Include account head mappings if provided
            if (accountHeadMappings) {
                formData.append('accountHeadMappings', JSON.stringify(accountHeadMappings));
            }

            // Include conflict headers if provided
            if (conflictHeaders && Object.keys(conflictHeaders).length > 0) {
                formData.append('conflictHeaders', JSON.stringify(conflictHeaders));
            }

            // Add weighted data if provided
            if (weightedData) {
                formData.append('weightedData', JSON.stringify(weightedData));
            }

            formData.append('getExpenseHeads', 'true');

            const response = await axios.post(
                `${API_BASE_URL}/analyze`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );

            if (response.data.success) {
                // Make sure expense heads don't include merged or hidden items
                if (accountHeadMappings) {
                    let filteredHeads = response.data.expenseHeads;

                    // If we have conflict headers, filter out source headers
                    if (conflictHeaders && Object.keys(conflictHeaders).length > 0) {
                        const sourceHeaders = Object.keys(conflictHeaders).map(h => h.toLowerCase());
                        filteredHeads = filteredHeads.filter(head =>
                            !sourceHeaders.includes(head.toLowerCase())
                        );
                    }

                    // Filter out hidden headers
                    filteredHeads = filteredHeads.filter(head => {
                        const headLower = head.toLowerCase();
                        return !accountHeadMappings[headLower] || accountHeadMappings[headLower] !== 'hide';
                    });

                    setExpenseHeads(filteredHeads);
                } else {
                    setExpenseHeads(response.data.expenseHeads);
                }

                setBusinessType(response.data.businessType);
                return true;
            } else {
                throw new Error(response.data.error || 'Error analyzing financial data');
            }
        } catch (err) {
            const message = err.response?.data?.error || err.message || 'Error analyzing financial data';
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    };

    // Updated analyzeFiles function with weighted data support
    const analyzeFiles = async (
        files,
        fileParams,
        bizType,
        selectedSDEs = [],
        accountHeadMappings = null,
        conflictHeaders = null,
        weightedData = null
    ) => {
        setLoading(true);
        setError(null);

        try {
            const formData = new FormData();

            files.forEach(file => {
                formData.append('files', file);
            });

            formData.append('fileParams', JSON.stringify(fileParams));
            if (bizType) {
                formData.append('bizType', bizType);
            }

            // Make sure we only send SDEs that are not hidden
            const visibleSDEs = accountHeadMappings
                ? selectedSDEs.filter(sde => {
                    const sdeLower = sde.toLowerCase();
                    if (typeof accountHeadMappings[sdeLower] === 'object') {
                        return accountHeadMappings[sdeLower].category !== 'hide';
                    }
                    return !accountHeadMappings[sdeLower] || accountHeadMappings[sdeLower] !== 'hide';
                })
                : selectedSDEs;

            formData.append('selectedSDEs', JSON.stringify(visibleSDEs));

            // Include account head mappings if provided
            if (accountHeadMappings) {
                formData.append('accountHeadMappings', JSON.stringify(accountHeadMappings));
            }

            // Include conflict headers if provided
            if (conflictHeaders && Object.keys(conflictHeaders).length > 0) {
                formData.append('conflictHeaders', JSON.stringify(conflictHeaders));
            }

            // Add weighted data if provided
            if (weightedData) {
                formData.append('weightedData', JSON.stringify(weightedData));
            }

            const response = await axios.post(
                `${API_BASE_URL}/analyze`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );

            if (response.data.success) {
                setFileId(response.data.fileId);
                setExcelData({
                    tableData: response.data.tableData,
                    columns: response.data.columns
                });
                setBusinessType(response.data.businessType);
                return true;
            } else {
                throw new Error(response.data.error || 'Error analyzing financial data');
            }
        } catch (err) {
            const message = err.response?.data?.error || err.message || 'Error analyzing financial data';
            setError(message);
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    };

    const downloadAnalysisExcel = async (customFilename = '') => {
        try {
            if (!fileId) {
                throw new Error('No Excel report available for download');
            }

            const defaultFilename = customFilename || `Financial_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`;

            const encodedFilename = encodeURIComponent(defaultFilename);

            window.location.href = `${API_BASE_URL}/download-excel/${fileId}?filename=${encodedFilename}`;
            return true;
        } catch (err) {
            const message = err.message || 'Error downloading Excel file';
            setError(message);
            throw new Error(message);
        }
    };

    return {
        loading,
        error,
        fileId,
        excelData,
        businessType,
        expenseHeads,
        accountHeads,
        accountHeadCategories,
        getAccountHeads,
        getExpenseHeads,
        analyzeFiles,
        downloadAnalysisExcel,
    };
};

export default useFinancialAPI;